/**
 * ComparisonView — top-level container for the Comparison module. It
 * orchestrates the multi-site picker + 7-tab strip and routes each tab
 * to its panel (F2–F6). Until a panel ships, its tab renders the
 * `ComparisonPlaceholder`.
 *
 *   ┌──────────────────────────────────────────┐
 *   │ Header (eval name + site count)          │
 *   ├──────────────────────────────────────────┤
 *   │ SiteMultiPicker (checkbox grid)          │
 *   ├──────────────────────────────────────────┤
 *   │ ComparisonTabs (Overview · Details · …)  │
 *   ├──────────────────────────────────────────┤
 *   │ Active panel content                     │
 *   └──────────────────────────────────────────┘
 *
 * URL state:
 *   - `?sites=a,b,c` — selected siteIds (round-trips on reload + share-link)
 *   - `?tab=scoring` — active tab key (preserved through page reload)
 *
 * Default selection: the top-3 sites by overall scoring rank (or the first
 * 3 sites alphabetically if no scoring summary is present yet).
 *
 * Tab switching preserves picker state because the picker lives here, not
 * inside the panels.
 *
 * Linear: MYD-216 (F1).
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useSearchParams } from 'react-router-dom'
import { GitCompareArrows } from 'lucide-react'

import { fetchSites } from 'slices/evaluationSiteSlice'
import { fetchScoringSummary } from 'slices/scoringSlice'

import SiteMultiPicker from './SiteMultiPicker'
import ComparisonTabs, { COMPARISON_TABS, ComparisonTabKey } from './ComparisonTabs'
import ComparisonPlaceholder from './ComparisonPlaceholder'
import ComparisonOverview from './ComparisonOverview'
import ComparisonDetails from './ComparisonDetails'
import ComparisonScoring from './ComparisonScoring'
import ComparisonFinancials from './ComparisonFinancials'
import ComparisonRisks from './ComparisonRisks'
import ComparisonProsCons from './ComparisonProsCons'
import ComparisonMap from './ComparisonMap'
import { isAskMdtAvailable } from 'features/AskMdt/availability'
import { EvaluationDraftPanel } from 'features/AskMdt/components/EvaluationDraftPanel'

interface ComparisonViewProps {
	evalId: string
}

const VALID_TAB_KEYS: ComparisonTabKey[] = COMPARISON_TABS.map((t) => t.key)

const parseSitesParam = (value: string | null): string[] => {
	if (!value) return []
	return value
		.split(',')
		.map((s) => s.trim())
		.filter(Boolean)
}

const ComparisonView = ({ evalId }: ComparisonViewProps) => {
	const dispatch = useDispatch()
	const [searchParams, setSearchParams] = useSearchParams()
	// "Draft comparison" (MYD-639), where Ask MDT is offered.
	const askAvailable = isAskMdtAvailable(useSelector((state: any) => state.userLogin?.userInfo))

	const { entities: siteEntities, byEvaluation } = useSelector(
		(state: any) => state.evaluationSites,
	)
	const summary = useSelector((state: any) => state.scoring?.summaryByEvaluation?.[evalId])

	const sites: any[] = useMemo(() => {
		const ids: string[] = byEvaluation[evalId] || []
		return ids.map((id) => siteEntities[id]).filter(Boolean)
	}, [byEvaluation, siteEntities, evalId])

	// Rankings map for picker badges (`#1`, `#2`…) and for default selection.
	const rankingsBySiteId: Record<string, number> = useMemo(() => {
		if (!summary?.sites?.length) return {}
		const sorted = summary.sites
			.slice()
			.sort((a: any, b: any) => {
				const aS = a.overallScore ?? -Infinity
				const bS = b.overallScore ?? -Infinity
				if (aS !== bS) return bS - aS
				return String(a.siteName ?? '').localeCompare(String(b.siteName ?? ''))
			})
		const map: Record<string, number> = {}
		sorted.forEach((row: any, idx: number) => {
			map[String(row.siteId)] = idx + 1
		})
		return map
	}, [summary])

	useEffect(() => {
		;(dispatch as any)(fetchSites(evalId))
		;(dispatch as any)(fetchScoringSummary(evalId))
	}, [dispatch, evalId])

	// Picker state (URL-backed).
	const selectedIds = useMemo(
		() => parseSitesParam(searchParams.get('sites')),
		[searchParams],
	)

	// Hydrate the URL with the default selection (top-3 ranked, else first 3
	// alphabetically) the first time we have sites and no `?sites=` param.
	useEffect(() => {
		if (sites.length === 0) return
		if (searchParams.has('sites')) return
		const ranked = sites
			.slice()
			.sort((a, b) => {
				const aRank = rankingsBySiteId[a._id] ?? Infinity
				const bRank = rankingsBySiteId[b._id] ?? Infinity
				if (aRank !== bRank) return aRank - bRank
				return String(a.name).localeCompare(String(b.name))
			})
		const defaults = ranked.slice(0, Math.min(3, ranked.length)).map((s) => s._id)
		if (defaults.length > 0) {
			const next = new URLSearchParams(searchParams)
			next.set('sites', defaults.join(','))
			setSearchParams(next, { replace: true })
		}
		// We deliberately depend on `sites` and `rankingsBySiteId` so the
		// hydration runs once data lands. searchParams is read but never written
		// in a way that would loop because we only set it when `sites` is absent.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sites, rankingsBySiteId])

	const setSelection = useCallback(
		(next: string[]) => {
			const params = new URLSearchParams(searchParams)
			if (next.length === 0) {
				params.delete('sites')
			} else {
				params.set('sites', next.join(','))
			}
			setSearchParams(params, { replace: false })
		},
		[searchParams, setSearchParams],
	)

	const handleToggle = useCallback(
		(siteId: string) => {
			const set = new Set(selectedIds)
			if (set.has(siteId)) set.delete(siteId)
			else set.add(siteId)
			setSelection(Array.from(set))
		},
		[selectedIds, setSelection],
	)

	const handleSelectAll = useCallback(() => {
		setSelection(sites.map((s) => s._id))
	}, [sites, setSelection])

	const handleClear = useCallback(() => {
		setSelection([])
	}, [setSelection])

	// Tab state (URL-backed).
	const tabParam = searchParams.get('tab') as ComparisonTabKey | null
	const activeTab: ComparisonTabKey =
		tabParam && VALID_TAB_KEYS.includes(tabParam) ? tabParam : 'overview'

	const setActiveTab = useCallback(
		(key: ComparisonTabKey) => {
			const params = new URLSearchParams(searchParams)
			if (key === 'overview') {
				params.delete('tab')
			} else {
				params.set('tab', key)
			}
			setSearchParams(params, { replace: false })
		},
		[searchParams, setSearchParams],
	)

	const selectedSites: any[] = useMemo(
		() => selectedIds.map((id) => siteEntities[id]).filter(Boolean),
		[selectedIds, siteEntities],
	)

	const canCompare = selectedSites.length >= 2
	const activeTabDef = COMPARISON_TABS.find((t) => t.key === activeTab)

	const renderActivePanel = () => {
		if (!canCompare) {
			return (
				<ComparisonPlaceholder
					title="Pick 2 or more sites to compare"
					description="Select sites in the picker above. The first time you visit /compare, the top-3 ranked sites are pre-selected."
				/>
			)
		}
		switch (activeTab) {
			case 'overview':
				return <ComparisonOverview evalId={evalId} sites={selectedSites} />
			case 'details':
				return <ComparisonDetails sites={selectedSites} />
			case 'scoring':
				return <ComparisonScoring evalId={evalId} sites={selectedSites} />
			case 'financials':
				return <ComparisonFinancials evalId={evalId} sites={selectedSites} />
			case 'risks':
				return <ComparisonRisks evalId={evalId} sites={selectedSites} />
			case 'proscons':
				return <ComparisonProsCons evalId={evalId} sites={selectedSites} />
			case 'map':
				return <ComparisonMap sites={selectedSites} />
		}
	}

	return (
		<div className="space-y-4">
			<div>
				<h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
					<GitCompareArrows className="w-5 h-5 text-[#0080fe]" />
					Compare sites
				</h3>
				<p className="text-sm text-gray-500">
					Select 2 or more sites and switch tabs to inspect each dimension side by side. Your
					selection and active tab are encoded in the URL so views are shareable.
				</p>
			</div>

			<SiteMultiPicker
				sites={sites}
				selectedIds={selectedIds}
				onToggle={handleToggle}
				onSelectAll={handleSelectAll}
				onClear={handleClear}
				rankingsBySiteId={rankingsBySiteId}
			/>

			{/* Keyed by the selection: a draft about other sites is not kept. */}
			{askAvailable && canCompare && (
				<EvaluationDraftPanel key={selectedIds.join(',')} evaluationId={evalId} kind="comparison" siteIds={selectedIds} />
			)}

			<div className="space-y-3">
				<ComparisonTabs active={activeTab} onChange={setActiveTab} />
				{renderActivePanel()}
			</div>
		</div>
	)
}

export default ComparisonView
